import { useEffect, useRef, useState } from "react";
import { ArrowRight, Smartphone } from "lucide-react";
import { PreferenceSwitchRow } from "@/features/legal/components/onboarding/PreferenceSwitchRow";
import { PREFERENCE_DEFINITIONS, PREFERENCES_COPY } from "@/features/legal/config/onboarding.config";
import { useSaveOnboardingPreferences } from "@/features/legal/hooks/useSaveOnboardingPreferences";
import type { OnboardingPreferences } from "@/features/legal/types/onboarding.type";
import { maskCellPhone } from "@/features/legal/utils/maskCellPhone";
import { ButtonWithLoader } from "@/shared/components/customButtons/ButtonWithSpinner";

type PreferencesStepProps = {
  initialPreferences: OnboardingPreferences
  cellPhoneNumber: string | null
  onSaved: (preferences: OnboardingPreferences) => void
};

const PREFERENCE_GROUPS = [
  { key: "cse", title: PREFERENCES_COPY.CSE_GROUP },
  { key: "yeati", title: PREFERENCES_COPY.YEATI_GROUP },
] as const;

const PhoneSummary = ({ maskedPhone }: { maskedPhone: string }) => (
  <div className="flex items-center gap-4 rounded-2xl bg-gray-50 p-4 dark:bg-gray-800">
    <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary" aria-hidden>
      <Smartphone size={20} />
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-xs text-gray-500 dark:text-gray-400">{PREFERENCES_COPY.PHONE_LABEL}</p>
      <p className="text-sm font-semibold tracking-wide text-gray-900 dark:text-white">{maskedPhone}</p>
    </div>
    <span className="rounded-full bg-success/15 px-3 py-1 text-xs font-medium text-emerald-800 dark:text-emerald-300">{PREFERENCES_COPY.PHONE_BADGE}</span>
  </div>
);

// Optional choices, independent from the CGU: existing values are preloaded
// and nothing is saved until the employee confirms; everything may stay off.
export const PreferencesStep = ({ initialPreferences, cellPhoneNumber, onSaved }: PreferencesStepProps) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [preferences, setPreferences] = useState<OnboardingPreferences>(initialPreferences);
  const { save, isSaving, hasSaveError } = useSaveOnboardingPreferences();
  const maskedPhone = maskCellPhone(cellPhoneNumber);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, []);

  const handleSave = () => save(preferences, { onSuccess: () => onSaved(preferences) });

  return (
    <div className="flex flex-col gap-6">
      <div className="space-y-3">
        <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold leading-tight tracking-tight text-gray-900 outline-none dark:text-white">
          {PREFERENCES_COPY.TITLE}
          {" "}
          <span className="block text-primary">{PREFERENCES_COPY.TITLE_ACCENT}</span>
        </h1>
        <p className="text-base leading-7 text-gray-600 dark:text-gray-300">{PREFERENCES_COPY.INTRO}</p>
      </div>
      {maskedPhone && <PhoneSummary maskedPhone={maskedPhone} />}
      {PREFERENCE_GROUPS.map((group) => (
        <div key={group.key}>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500">{group.title}</p>
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {PREFERENCE_DEFINITIONS.filter((definition) => definition.group === group.key).map((definition) => (
              <PreferenceSwitchRow
                key={definition.field}
                id={definition.field}
                title={definition.title}
                description={definition.description}
                checked={preferences[definition.field]}
                disabled={isSaving}
                onChange={(checked) => setPreferences((previous) => ({ ...previous, [definition.field]: checked }))}
              />
            ))}
          </div>
        </div>
      ))}
      <p className="text-xs text-gray-500 dark:text-gray-400">{PREFERENCES_COPY.FOOTNOTE}</p>
      {hasSaveError && <p role="alert" className="text-sm text-error">{PREFERENCES_COPY.SAVE_ERROR}</p>}
      <ButtonWithLoader
        pill
        color="primary"
        className="self-start"
        isLoading={isSaving}
        disabled={isSaving}
        onClick={handleSave}
        data-testid="preferences-save-button"
      >
        <span className="flex items-center gap-2">
          {PREFERENCES_COPY.SAVE}
          <ArrowRight size={16} aria-hidden />
        </span>
      </ButtonWithLoader>
    </div>
  );
};

PreferencesStep.displayName = "PreferencesStep";
